"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ReactNode } from "react";
import { api } from "@/lib/api";
import { getAuthToken, clearAuth } from "@/lib/auth";
import type { User } from "@/types/user";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Only a signed-out answer ends the session. A timeout or server error while
  // Wake Speed is busy keeps the session and tries again, so a slow moment
  // never signs anyone out.
  const refreshUser = useCallback(async () => {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    for (let attempt = 0; ; attempt++) {
      const response = await fetch("/api/auth/me", { headers, credentials: "same-origin" }).catch(() => null);
      // undefined: no readable answer yet. null or no user: signed out.
      const data = response?.ok
        ? ((await response.json().catch(() => undefined)) as { user?: User | null } | null | undefined)
        : undefined;
      if (data?.user) {
        if (data.user.role.includes("kiosk")) {
          clearAuth();
          setUser(null);
        } else {
          setUser(data.user);
        }
        setLoading(false);
        return;
      }
      if (data !== undefined || (response && [401, 403, 404].includes(response.status))) {
        clearAuth();
        setUser(null);
        setLoading(false);
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * 2 ** attempt, 15000)));
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore logout errors
    }
    clearAuth();
    setUser(null);
    window.location.href = "/login";
  }, []);

  const can = useCallback(
    (permission: string) => {
      if (!user) return false;
      if (user.role.includes("developer")) return true;
      // TEMP: New social tools stay developer-only while they are still
      // being built. Email/newsletter tools are controlled by permissions.
      if (permission.startsWith("marketing.social.") && !user.role.includes("developer")) {
        return false;
      }
      if (permission === "accounting.manage") {
        return user.permissions.includes(permission);
      }
      if (user.role.includes("admin")) return true;
      return user.permissions.includes(permission);
    },
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, loading, logout, refreshUser, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
